import { Link } from "react-router-dom";
import { SeoTags } from "../../../components/Head/SeoTags";
import DuplicateMock from "../../../components/Mockups/DuplicateMock";

const APP_URL = process.env.REACT_APP_APP_URL;

const DuplicateDetection = () => {
  return (
    <>
      <SeoTags
        title="Duplicate Detection - Never Save the Same Link Twice"
        description="onBookmarks warns you when you're saving a link you already have, skips duplicates during bulk imports, and scans your whole library for existing duplicates."
        keywords="duplicate bookmarks, duplicate detection, bookmark deduplication, clean up bookmarks, duplicate bookmark finder, remove duplicate bookmarks"
        canonical="https://onbookmarks.com/features/duplicate-detection/"
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Duplicate Detection - Never Save the Same Link Twice",
            description:
              "Get warned when saving a link you already have, skip duplicates on bulk import, and scan your library for existing duplicates.",
            url: "https://onbookmarks.com/features/duplicate-detection/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Duplicate warning while saving",
                "Shows where the link already exists",
                "Bulk imports skip duplicates automatically",
                "Library-wide duplicate scan",
                "Dead-link detection with Wayback recovery",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://onbookmarks.com" },
                { "@type": "ListItem", position: 2, name: "Features", item: "https://onbookmarks.com/features/" },
                { "@type": "ListItem", position: 3, name: "Duplicate Detection", item: "https://onbookmarks.com/features/duplicate-detection/" },
              ],
            },
          }),
        }}
      />

      {/* Hero */}
      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    <i className="bi bi-files me-2"></i>
                    Clean Library, Automatically
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  Never save the same link twice
                </h1>
                <p className="lead">
                  Save a URL you already have, and onBookmarks shows you the
                  existing bookmark on the spot (where it lives, when you saved
                  it) before a duplicate is ever created.
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <Link
                    to={`${APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    Start Free
                  </Link>
                  <Link
                    to="/features/cleanup"
                    className="btn btn-outline-primary btn-lg px-4"
                  >
                    Library Cleanup
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <DuplicateMock />
            </div>
          </div>
        </div>
      </section>

      {/* Three layers */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                Three layers of duplicate defense
              </h2>
              <p className="lead text-muted">
                Duplicates are caught when you save, when you import, and even
                the ones that are already in your library.
              </p>
            </div>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-plus-circle fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">While you save</h3>
                  <p className="text-muted mb-0">
                    Paste a URL you've saved before and the existing bookmark
                    appears right in the form (with its collections) so you can
                    jump to it instead of duplicating it.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-cloud-upload fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">During bulk imports</h3>
                  <p className="text-muted mb-0">
                    Drop in a list of links or import a bookmarks file, anything
                    you already have is flagged "already exists" and skipped
                    automatically.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <i className="bi bi-search fs-3 text-purple mb-3"></i>
                  <h3 className="h5 mb-2">Across your whole library</h3>
                  <p className="text-muted mb-0">
                    Years of accumulated duplicates? The built-in{" "}
                    <Link to="/features/cleanup">library cleanup</Link> scans
                    everything, finds duplicates and dead links, and helps you
                    clear them in one pass.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="h1 fw-bold mb-4">
                Why duplicate detection matters
              </h2>
              <p className="lead mb-4">
                A duplicate isn't just clutter. It splits your own history.
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">One source of truth per link</h4>
                    <p className="text-muted mb-0">
                      Notes, tags, and reading history stay on one bookmark
                      instead of scattering across copies.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Search results you can trust</h4>
                    <p className="text-muted mb-0">
                      No more guessing which of the three copies is the one with
                      your notes on it.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Need it in two places? Use collections</h4>
                    <p className="text-muted mb-0">
                      One bookmark can live in{" "}
                      <Link to="/features/multi-collection">
                        multiple collections
                      </Link>: the right way to file something twice without
                      copying it.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="bg-white rounded-4 shadow-sm p-5 text-center">
                <i className="bi bi-files display-1 text-purple mb-3"></i>
                <p className="h5 mb-2">Caught before it happens</p>
                <p className="text-muted mb-0">
                  The best duplicate cleanup is the one you never have to do.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center">
            <h2 className="h1 fw-bold mb-4 text-white">
              Keep your library clean from day one
            </h2>
            <p className="lead mb-4 text-white">
              Import your existing bookmarks, duplicates get caught on the way
              in.
            </p>
            <Link to={`${APP_URL}/register`} className="btn btn-light btn-lg px-5">
              Start Free
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DuplicateDetection;
